import { useContext, useEffect, useRef } from "react";
import { FormattedMessage, IntlProvider } from "react-intl";
import homeMessages from "../translate/homeMessages";
import { LangContext, iconSize } from "../App";
import Logo600 from "../images/logo600.png";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import { motion, AnimatePresence } from "framer-motion";
import { textVariants } from "../components/textVariants";
import { Helmet } from "react-helmet-async";
import Me from "../images/DSC_1652.JPG";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  Link as MuiLink,
} from "@mui/material";

const techTags = [
  "React.js",
  "PHP",
  "Node.js",
  "TypeScript",
  "REST API",
  "Cloud",
  "Bootstrap",
  "Material UI",
  "MySQL",
  "Postfix",
  "MSSQL",
  "VPS",
  "DNS",
  "SEO",
  "Docker",
  "GIT",
  "CircleCI",
];

const Home = () => {
  const language = useContext(LangContext);
  const imgRef = useRef(null);

  useEffect(() => {
    fetch("https://api.ipify.org?format=json")
      .then((response) => response.json())
      .then((data) => {
        return fetch(
          "https://michaljaskulski.pl/api/address.php?ipAddress=" + data.ip,
        );
      })
      .catch((error) => console.error("Błąd:", error));
  }, []);

  const styles = {
    wrapper: {
      minHeight: "100vh",
      padding: "80px 24px 60px 24px",
      fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
    },
    container: {
      maxWidth: "1200px",
      margin: "0 auto",
      width: "100%",
    },
    headerZone: {
      textAlign: "center",
      marginBottom: "48px",
    },
    sectionSubtitle: {
      fontSize: "14px",
      fontWeight: "700",
      color: "#4f46e5",
      textTransform: "uppercase",
      letterSpacing: "0.1em",
      marginBottom: "8px",
    },
    mainTitle: {
      fontSize: "36px",
      fontWeight: "800",
      color: "#0f172a",
      letterSpacing: "-0.03em",
      margin: 0,
    },
    splitLayout: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(min(450px, 100%), 1fr))",
      gap: "32px",
      alignItems: "start",
    },
    competenceCard: {
      background: "#ffffff",
      borderRadius: "24px",
      padding: "40px",
      border: "1px solid #e2e8f0",
      boxShadow: "0 10px 30px -15px rgba(0,0,0,0.05)",
      boxSizing: "border-box",
    },
    cardHeader: {
      display: "flex",
      alignItems: "center",
      gap: "16px",
      marginBottom: "28px",
      borderBottom: "1px solid #f1f5f9",
      paddingBottom: "20px",
    },
    iconWrapper: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "48px",
      height: "48px",
      borderRadius: "12px",
      backgroundColor: "#eef2ff",
      color: "#4f46e5",
    },
    cardTitle: {
      fontSize: "18px",
      fontWeight: "700",
      color: "#0f172a",
      margin: 0,
    },
    list: {
      display: "flex",
      flexDirection: "column",
      gap: "16px",
    },
    listItem: {
      display: "flex",
      alignItems: "flex-start",
      gap: "12px",
      fontSize: "15px",
      color: "#475569",
      lineHeight: "1.5",
    },
    checkIcon: {
      flexShrink: 0,
      marginTop: "3px",
      color: "#10b981",
    },
  };

  const renderFeatureCards = () => {
    if (language === "pl") {
      return (
        <>
          <FeatureCard title="React Frontend">
            Nowoczesne aplikacje SPA, panele administracyjne i systemy
            biznesowe.
          </FeatureCard>
          <FeatureCard title="Backend & API">
            PHP, REST API, integracje z systemami zewnętrznymi.
          </FeatureCard>
          <FeatureCard title="Bazy danych">
            MySQL, MSSQL, optymalizacja wydajności.
          </FeatureCard>
          <FeatureCard title="VPS & DevOps">
            Docker, Linux, SSL, kopie bezpieczeństwa.
          </FeatureCard>
          <FeatureCard title="Projektowanie i architektura">
            Struktura aplikacji, dobór technologii, skalowalność i czytelny kod.
          </FeatureCard>
          <FeatureCard title="Bezpieczeństwo aplikacji">
            Walidacja danych, bezpieczne API, uprawnienia użytkowników i ochrona
            danych.
          </FeatureCard>
          <FeatureCard title="Wdrożenia i utrzymanie">
            Publikacja aplikacji, konfiguracja środowiska produkcyjnego i
            monitoring działania.
          </FeatureCard>
          <FeatureCard title="IT Management">
            Organizacja pracy, zarządzanie infrastrukturą IT i kompleksowa
            realizacja projektów.
          </FeatureCard>
        </>
      );
    }

    return (
      <>
        <FeatureCard title="Comprehensive IT solutions">
          From initial concept to production release. I build software products
          that truly empower businesses and adapt to their long-term growth.
        </FeatureCard>
        <FeatureCard title="Tailored Web Applications">
          Designing and implementing modern web solutions tailored precisely to
          your internal workflows – swift, seamless, and scalable.
        </FeatureCard>
        <FeatureCard title="Cloud Systems">
          Deploying custom cloud architectures that guarantee robust uptime,
          industry-standard security practices, and flexible cost efficiency.
        </FeatureCard>
        <FeatureCard title="Secure Backend Architectures">
          Engineering fast, reliable backend microservices and RESTful APIs,
          securing entry points, and focusing strictly on data optimization.
        </FeatureCard>
      </>
    );
  };

  return (
    <>
      <Helmet>
        <title>Fullstack React Developer Michał Jaskulski</title>
        <meta
          name="description"
          content="Projektowanie i wdrażanie aplikacji webowych dostosowanych do potrzeb biznesowych"
        />
      </Helmet>

      <Box
        className="bodyBackground"
        sx={{
          backgroundColor: "#f8fafc",
          minHeight: "100vh",
          px: 3,
          pt: 10,
          pb: 5,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <IntlProvider locale={language} messages={homeMessages[language]}>
          <AnimatePresence>
            <Container
              component={motion.div}
              maxWidth="lg"
              initial="hidden"
              animate="visible"
              exit="hidden"
              variants={textVariants}
              disableGutters
            >
              <Box
                sx={{
                  textAlign: "center",
                  mb: 7,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: "32px", md: "42px" },
                    fontWeight: 800,
                    color: "#0f172a",
                    letterSpacing: "-0.03em",
                    mb: 1,
                  }}
                >
                  <h1 style={styles.mainTitle}>
                    <FormattedMessage id="fullstack" />
                  </h1>
                  <h1 style={styles.mainTitle}>
                    <FormattedMessage id="fullstackMJ" />
                  </h1>
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: "20px",
                    fontWeight: 800,
                    color: "#0f172a",
                    letterSpacing: "-0.03em",
                  }}
                ></Typography>
              </Box>

              <Grid container spacing={6} alignItems="flex-start">
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      position: { xs: "static", md: "sticky" },
                      top: 40,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                      gap: 3,
                      bgcolor: "#ffffff",
                      borderRadius: 6,
                      p: 5,
                      border: "1px solid #e2e8f0",
                      boxShadow: "0 10px 30px -15px rgba(0,0,0,0.03)",
                    }}
                  >
                    <Box
                      sx={{
                        width: "90%",
                        aspectRatio: "1/1",
                        overflow: "hidden",
                        boxShadow: "0 20px 40px -15px rgba(79, 70, 229, 0.2)",
                        border: "4px solid #ffffff",
                        outline: "1px solid #e2e8f0",
                      }}
                    >
                      <Box
                        component="img"
                        ref={imgRef}
                        src={Me}
                        alt="Michał Jaskulski - Fullstack React Developer"
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </Box>
                    <Box sx={{ textAlign: "center" }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          gap: 1.5,
                          flexWrap: "wrap",
                          mt: 2,
                        }}
                      >
                        <StatCard number="20+" label="lat w IT" />
                        <StatCard number="40+" label="projektów" />
                        <StatCard
                          number="Fullstack"
                          label="fontend i backend"
                        />
                        <StatCard
                          number="React"
                          label="nowoczesne aplikacje webowe"
                        />
                        <StatCard
                          number="End-to-End"
                          label="od projektu do wdrożenia"
                        />
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                        justifyContent: "center",
                        mt: 1.5,
                      }}
                    >
                      {techTags.map((tag) => (
                        <Chip
                          key={tag}
                          label={tag}
                          size="small"
                          sx={{
                            backgroundColor: "#f1f5f9",
                            color: "#334155",
                            borderRadius: "8px",
                            fontWeight: 600,
                            fontSize: "13px",
                          }}
                        />
                      ))}
                    </Box>
                    <Button
                      variant="contained"
                      href="/kontakt"
                      sx={{
                        backgroundColor: "#0f172a",
                        color: "#ffffff",
                        textTransform: "none",
                        px: 3,
                        py: 1.5,
                        borderRadius: "12px",
                        fontSize: "14px",
                        fontWeight: 600,
                        mt: 2,
                        boxShadow: "0 4px 12px rgba(15, 23, 42, 0.15)",
                        "&:hover": {
                          backgroundColor: "#1e293b",
                        },
                      }}
                    >
                      {language === "pl"
                        ? "Porozmawiajmy o projekcie"
                        : "Let's talk about your project"}
                    </Button>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        md: "repeat(2, minmax(0, 1fr))",
                      },
                      gap: 3,
                    }}
                  >
                    {renderFeatureCards()}
                  </Box>
                </Grid>
              </Grid>
            </Container>
          </AnimatePresence>
        </IntlProvider>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 2.5,
            flexWrap: "wrap",
            mt: 5,
          }}
        >
          {Logo600 && (
            <Box
              component="img"
              src={Logo600}
              alt="Logo firmy"
              sx={{
                maxWidth: "180px",
                height: "auto",
                opacity: 0.8,
              }}
            />
          )}
          <MuiLink
            href="/fullstack-developer-slask"
            underline="none"
            sx={{
              color: "#64748b",
              fontSize: "13px",
              fontWeight: 500,
              transition: "color 0.2s ease",
              "&:hover": {
                color: "#334155",
              },
            }}
          >
            Fullstack Developer - województwo śląskie
          </MuiLink>
        </Box>
      </Box>
    </>
  );
};
const FeatureCard = ({ title, children }) => (
  <Card
    elevation={0}
    sx={{
      background: "#ffffff",
      borderRadius: "16px",
      p: 1,
      boxShadow: "0 4px 20px -2px rgba(0,0,0,0.02)",
      border: "1px solid #e2e8f0",
    }}
  >
    <CardContent sx={{ p: "16px !important" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          mb: 1,
        }}
      >
        <AutoAwesomeRoundedIcon sx={{ ...iconSize, color: "#c9a66b" }} />
        <Typography
          variant="h6"
          sx={{
            fontSize: "16px",
            fontWeight: 700,
            color: "#0f172a",
          }}
        >
          {title}
        </Typography>
      </Box>
      <Typography
        variant="body2"
        sx={{
          fontSize: "15px",
          color: "#475569",
          lineHeight: 1.6,
        }}
      >
        {children}
      </Typography>
    </CardContent>
  </Card>
);
const StatCard = ({ number, label }) => (
  <Box
    sx={{
      background: "#f8fafc",
      border: "1px solid #e2e8f0",
      borderRadius: "12px",
      py: 1.25,
      px: 2,
      textAlign: "center",
      minWidth: "90px",
    }}
  >
    <Typography
      sx={{
        fontSize: "24px",
        fontWeight: 800,
        color: "#4f46e5",
        lineHeight: 1.2,
      }}
    >
      {number}
    </Typography>
    <Typography
      sx={{
        fontSize: "12px",
        color: "#64748b",
      }}
    >
      {label}
    </Typography>
  </Box>
);
export default Home;
